import React, {useEffect, useState} from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {PaginationProvider} from 'react-bootstrap-table2-paginator';
import {Pagination} from "./pagination/Pagination";
import {NoRecordsFoundMessage, PleaseWaitMessage} from "./pagination/TablePaginationHelpers";
import {genericGetAll} from "../crud";
import {sizePerPageList} from "../InitialStates";
import {Formik} from "formik";
import {KTIcon} from "../../../../_metronic/helpers/components/KTIcon";

const TABLE_PAGE_SIZE_KEY = "bedrock_table_page_size";

// Get default page size from localStorage or fall back to prop value
const getDefaultPageSize = (propPageSize) => {
    const stored = localStorage.getItem(TABLE_PAGE_SIZE_KEY);
    if (stored) {
        const parsed = parseInt(stored);
        if ([10, 25, 50, 100].includes(parsed)) {
            return parsed;
        }
    }
    return propPageSize;
};

export function GenericTable({reload, defaultData, columns, entityId, idColumn = 'id', fixedFilterData = null, fixedFilterColumn = null, extraFilters = null, pageSize = 10, sortBy = "updatedOn", sortOrder = "desc"}) {
    const [dataList, setDataList] = useState(defaultData);
    const [isLoading, setIsLoading] = useState(false);
    const [sizePerPage, setSizePerPage] = useState(getDefaultPageSize(pageSize));
    const [page, setPage] = useState(1);
    const [searchField, setSearchField] = useState({text: "", dataField: ""});
    const [tableFilterData, setTableFilterData] = useState({});

    // Optional additional server-side filters, e.g. {filterColumn2, filterData2, filterOp2}
    const withExtraFilters = (params) => (extraFilters ? {...params, ...extraFilters} : params);

    useEffect(() => {
        setSearchField(getObjectWithSearchTrue());
    }, [columns]);

    useEffect(() => {

        setIsLoading(true);
        tableFilterData.page = page;
        tableFilterData.sizePerPage = sizePerPage;
        tableFilterData.currentIndex = 0;

        if (fixedFilterColumn) {
            tableFilterData.fixedFilterData = fixedFilterData;
            tableFilterData.fixedFilterColumn = fixedFilterColumn;
        }

        tableFilterData.sortBy = sortBy;
        tableFilterData.sortOrder = sortOrder;

        genericGetAll(entityId, withExtraFilters(tableFilterData)).then(
            ({data}) => {
                setDataList(data);
                setIsLoading(false);
                setPage(1);
            })
            .catch(() => {
                setIsLoading(false);
            });
    }, [reload, tableFilterData, entityId, sortBy, sortOrder]);

    const handleTableChange = (filterData, newState) => {

        filterData = newState.filters;

        filterData.sizePerPage = newState.sizePerPage;

        if (filterData.page === undefined) {
            filterData.page = newState.page;
        }

        if (filterData.page === undefined) {
            filterData.page = 1;
        }

        if (filterData.page === 0) {
            filterData.page = 1;
        }

        if (filterData.sizePerPage === undefined) {
            filterData.sizePerPage = newState.sizePerPage;
        }

        const currentIndex = (filterData.page - 1) * filterData.sizePerPage;

        setIsLoading(true);
        tableFilterData.page = filterData.page;
        tableFilterData.sizePerPage = filterData.sizePerPage;
        tableFilterData.currentIndex = currentIndex;

        genericGetAll(entityId, withExtraFilters(tableFilterData)).then().then(
            ({data}) => {
                setDataList(data);
                setSizePerPage(filterData.sizePerPage);
                setIsLoading(false);
                setPage(filterData.page);
            })
            .catch(() => {
                setIsLoading(false);
            });
    };

    const paginationOptions = {
        custom: true,
        totalSize: dataList.totalCount,
        sizePerPageList: sizePerPageList,
        sizePerPage: sizePerPage,
        page: page,
    };

    function getObjectWithSearchTrue() {
        for (let i = 0; i < columns.length; i++) {
            if (columns[i].search === true) {
                return columns[i];
            }
        }

        return null;
    }

    return (
        <>
            {searchField &&
                <Formik
                    initialValues={{
                        searchText: "",
                    }}
                    onSubmit={(values) => {

                        tableFilterData.searchString = values.searchText;
                        tableFilterData.searchField = searchField.dataField;

                        setTableFilterData(tableFilterData);
                        tableFilterData.page = page;
                        tableFilterData.sizePerPage = sizePerPage;
                        tableFilterData.currentIndex = 0;

                        setIsLoading(true);

                        genericGetAll(entityId, withExtraFilters(tableFilterData)).then(
                            ({data}) => {
                                setDataList(data);
                                setIsLoading(false);
                                setPage(1);
                            })
                            .catch(() => {
                                setIsLoading(false);
                            });
                    }}>
                    {({
                          values,
                          handleSubmit,
                          handleBlur,
                          setFieldValue,
                      }) => (
                        <form onSubmit={handleSubmit} className="form px-9 pt-8 pb-4">
                            <div className="col-lg-4">
                                <div className="input-group input-group-solid">
                                    <span className="input-group-text border-0 ps-4">
                                        <KTIcon iconName="magnifier" className="fs-3 text-gray-500" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control form-control-solid border-0 ps-1"
                                        name="searchText"
                                        placeholder={`Search by ${searchField.text}...`}
                                        onBlur={handleBlur}
                                        value={values.searchText}
                                        onChange={(e) => {
                                            setFieldValue("searchText", e.target.value);
                                            handleSubmit();
                                        }}
                                    />
                                    {values.searchText && (
                                        <span
                                            className="input-group-text border-0 pe-4"
                                            style={{cursor: "pointer"}}
                                            onClick={() => {
                                                setFieldValue("searchText", "");
                                                handleSubmit();
                                            }}
                                        >
                                            <KTIcon iconName="cross-circle" className="fs-3 text-gray-400" />
                                        </span>
                                    )}
                                </div>
                            </div>
                        </form>
                    )}
                </Formik>
            }

            <PaginationProvider pagination={paginationFactory(paginationOptions)}>
                {({paginationProps, paginationTableProps}) => {
                    return (
                        <Pagination

                            isLoading={isLoading}
                            paginationProps={paginationProps}>
                            <BootstrapTable
                                bordered={false}
                                wrapperClasses="table-responsive table align-middle table-row-bordered table-row-solid gy-4 gs-9"
                                headerClasses="fs-5 fs-8 fw-bold text-gray-400 text-uppercase"
                                bodyClasses="fw-6 fw-semibold text-gray-600 align-middle"
                                bootstrap4
                                remote
                                keyField={idColumn}
                                data={dataList.entities === null ? [] : dataList.entities}
                                columns={columns}
                                onTableChange={handleTableChange}
                                {...paginationTableProps}>
                                <PleaseWaitMessage entities={dataList.entities}/>
                                <NoRecordsFoundMessage entities={dataList.entities}/>
                            </BootstrapTable>
                        </Pagination>
                    );
                }}
            </PaginationProvider>
        </>
    );
}
